// client/src/pages/ShowingFeedbackPage.js — the tokenised feedback link sent after a showing
// (/showing-feedback/:token). Three questions: how interested, the price, comments. The
// answers go to the listing agent; the seller sees them only in a summary the agent edits
// and releases. API: /api/public/showings/feedback/:token
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

const inputCls = 'mt-1 block w-full rounded-md border border-slate-400 bg-white px-3 py-2 text-slate-900 focus:border-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-600';

export default function ShowingFeedbackPage() {
    const { token } = useParams();
    const [info, setInfo] = useState(null);
    const [error, setError] = useState('');
    const [f, setF] = useState({ interest_level: '', price_opinion: '', comments: '' });
    const [state, setState] = useState({ sending: false, done: '' });

    useEffect(() => {
        fetch(`/api/public/showings/feedback/${encodeURIComponent(token)}`)
            .then(async (r) => { const j = await r.json(); if (!r.ok) throw new Error(j.message || 'This link is not valid.'); return j; })
            .then(setInfo).catch((e) => setError(e.message));
    }, [token]);

    const submit = async (e) => {
        e.preventDefault();
        setState({ sending: true, done: '' }); setError('');
        try {
            const r = await fetch(`/api/public/showings/feedback/${encodeURIComponent(token)}`, { method: 'POST',
                headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(f) });
            const j = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(j.message || 'Could not save.');
            setState({ sending: false, done: j.message });
        } catch (err) { setState({ sending: false, done: '' }); setError(err.message); }
    };

    return (
        <div className="container mx-auto max-w-xl px-4 py-10 sm:px-6">
            <h1 className="text-2xl font-bold text-slate-900">Showing feedback</h1>
            {error && <p className="mt-4 text-red-700" role="alert">{error}</p>}
            {!info && !error && <p className="mt-4 text-slate-700" role="status">Loading…</p>}
            {info && info.submitted && !state.done && <p className="mt-4 text-slate-800">Thank you — feedback for this showing has already been received.</p>}
            {state.done && <p className="mt-4 rounded-md bg-emerald-50 p-4 text-emerald-900" role="status">{state.done}</p>}
            {info && !info.submitted && !state.done && (
                <form onSubmit={submit} className="mt-4 space-y-4">
                    <p className="text-slate-700">{[info.property.street_address, info.property.city].filter(Boolean).join(', ')} — {info.label}</p>
                    <fieldset>
                        <legend className="text-sm font-medium text-slate-800">How interested {info.recipient_kind === 'showing_agent' ? 'is your buyer' : 'are you'}?</legend>
                        {Object.entries(info.interest).map(([k, label]) => (
                            <label key={k} className="mt-1 flex items-center gap-2 text-slate-900">
                                <input type="radio" name="interest" required checked={f.interest_level === k} onChange={() => setF({ ...f, interest_level: k })} /> {label}
                            </label>
                        ))}
                    </fieldset>
                    <fieldset>
                        <legend className="text-sm font-medium text-slate-800">The asking price</legend>
                        {Object.entries(info.price).map(([k, label]) => (
                            <label key={k} className="mt-1 flex items-center gap-2 text-slate-900">
                                <input type="radio" name="price" checked={f.price_opinion === k} onChange={() => setF({ ...f, price_opinion: k })} /> {label}
                            </label>
                        ))}
                    </fieldset>
                    <div>
                        <label htmlFor="fb-comments" className="block text-sm font-medium text-slate-800">Comments for the seller (optional)</label>
                        <textarea id="fb-comments" rows={4} maxLength={2000} className={inputCls} value={f.comments} onChange={(e) => setF({ ...f, comments: e.target.value })} />
                        <p className="mt-1 text-xs text-slate-600">Please comment on the property, not on people or the neighbourhood's residents.</p>
                    </div>
                    <button type="submit" disabled={state.sending} className="rounded-md bg-slate-900 px-4 py-2 font-semibold text-white disabled:opacity-60">{state.sending ? 'Sending…' : 'Send feedback'}</button>
                </form>
            )}
        </div>
    );
}
